import Head from "@modules/common/components/head"
import Layout from "@modules/layout/templates"
import { ReactElement, useState } from "react"
import { NextPageWithLayout } from "types/global"
import { FieldValues, useForm } from "react-hook-form"
import Input from "@modules/common/components/input"
import Button from "@modules/common/components/button"
import Textarea from "@modules/common/components/textarea"
import { MEDUSA_BACKEND_URL } from "@lib/config"
import ContactElement from "@modules/common/components/contact-element"

interface contactCredentials extends FieldValues {
    first_name: string
    last_name: string
    email: string
    phone?: string
    message: string
}

const Home: NextPageWithLayout = () => {
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | undefined>(undefined)
    const [success, setSuccess] = useState<string | undefined>(undefined)
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm<contactCredentials>()

    const onSubmit = handleSubmit(async (data) => {
        setSubmitting(true)
        setError(undefined)

        const payload = {
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            phone: data.phone ?? "",
            message: data.message
        }
        const url = `${MEDUSA_BACKEND_URL}store/contact`

        fetch(url, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        })
            .then((response) => response.json())
            .then((data) => {
                //console.log(data)
                if (data.error === null || data.error.length < 1) {
                    reset({
                        email: "",
                        first_name: "",
                        last_name: "",
                        phone: "",
                        message: ""
                    })
                    setSuccess(
                        "Dankuwel voor uw bericht. We zullen zo snel mogelijk contact met u opnemen."
                    )
                    setSubmitting(false)
                } else setError(data.error)
                setSubmitting(false)
            })
            .catch((error) => {
                console.error(error)
                setError("Er is iets misgegaan. Probeer het later opnieuw.")
                setSubmitting(false)
            })
    })
    return (
        <>
            <Head
                title="Contact | Akupanel Store"
                description="Neem contact met ons op voor vragen of ondersteuning. Ons team staat klaar om u te helpen bij al uw behoeften. We horen graag van u!"
            ></Head>
            <ContactElement
                image={
                    "https://ik.imagekit.io/ik8qpq4jb/content/6a67c255849547d462cc34dbf33db0be.png"
                }
                alt={"Design keuken met akoestische panelen"}
                caption={""}
                title={"Vragen of wilt u advies?"}
                content={
                    <>
                        <p className={"text-sm mb-3 renderitem"}>
                            <strong>Magazijn</strong>
                            <br />
                            Verdunplein 17
                            <br />
                            5627 SZ
                            <br />
                            Eindhoven, Nederland
                            <br />
                            <small>Let op: dit is geen bezoek adres!</small>
                            <br />
                            <br />
                            <strong>Email:</strong>
                            <br />
                            <a href="mailto:team@akupanel-store.nl">
                                team@akupanel-store.nl
                            </a>
                            <br />
                            <hr />
                            <br />
                            <strong>Toch liever snel contact?</strong>
                            <br />
                            Start de chat rechts onderin en wij proberen u z.s.m
                            te helpen!
                        </p>
                    </>
                }
                subtitle={
                    <>
                        <h2 className="text-xl md:text-2xl lg:text-3xl 2xl:text-4xl font-display font-base subpixel-antialiased mb-6">
                            Contact
                        </h2>
                        <form
                            className="w-full flex flex-col mb-10 md:mb-0"
                            onSubmit={onSubmit}
                            id={"contactform"}
                        >
                            <div className="flex flex-col w-full gap-y-2">
                                <Input
                                    label="Voornaam"
                                    {...register("first_name", {
                                        required: "First name is required"
                                    })}
                                    autoComplete="given-name"
                                    errors={errors}
                                    required={true}
                                />
                                <Input
                                    label="Achternaam"
                                    {...register("last_name", {
                                        required: "Last name is verplicht"
                                    })}
                                    autoComplete="family-name"
                                    errors={errors}
                                    required={true}
                                />
                                <Input
                                    label="Email"
                                    {...register("email", {
                                        required: "Email is verplicht",
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: "Ongeldig e-mailadres"
                                        }
                                    })}
                                    autoComplete="email"
                                    errors={errors}
                                    required={true}
                                />
                                <Input
                                    label="Telefoonnummer"
                                    {...register("phone")}
                                    autoComplete="tel"
                                    errors={errors}
                                />
                                <Textarea
                                    label="Vraag of opmerking"
                                    {...register("message", {
                                        required: "Message is verplicht"
                                    })}
                                    errors={errors}
                                    required={true}
                                />
                            </div>
                            {error && (
                                <div>
                                    <span className="text-rose-500 w-full text-small-regular py-2 font-mono">
                                        Vul alle velden correct in.
                                    </span>
                                </div>
                            )}
                            {success && (
                                <div className="text-green-500 text-base-regular py-2 font-mono">
                                    {success}
                                </div>
                            )}
                            <Button className="mt-6">Versturen</Button>
                        </form>
                    </>
                }
                cta={{
                    text: "Vul het formulier in",
                    url: "#contactform"
                }}
                entranceAnimation={true}
            />
        </>
    )
}

Home.getLayout = (page: ReactElement) => {
    return <Layout>{page}</Layout>
}

export default Home
